// src/components/HandbookBot.jsx
// Public (unauthenticated) AI assistant for the King's Academy Student Handbook.
// Route: /handbook  --  must be registered OUTSIDE the auth guard in App.js.
//
// Backend: handbookChat Cloud Function (public, rate-limited, origin-allowlisted).
// PDF export: jsPDF (already a project dependency).

import React, { useState, useRef, useEffect, useCallback } from "react";
import jsPDF from "jspdf";

const ENDPOINT =
  "https://us-central1-kings-app-74847.cloudfunctions.net/handbookChat";

// King's Academy brand
const KA_RED = "#E83530";
const KA_NAVY = "#233C5F";
const KA_GOLD = "#A29164";

const MAX_CHARS = 1000;

const SUGGESTED = [
  "What is the mobile phone policy?",
  "What are the dress code requirements?",
  "How do I request an extension on an assessment?",
  "How are MOPs earned and what are the consequences?",
  "What are the graduation requirements?",
  "Who do I contact if I'm sick for more than one day?",
];

const GREETING = {
  role: "assistant",
  content:
    "Hello! I'm the King's Academy Student Handbook assistant. Ask me anything about school rules, academics, boarding life, athletics, or student support, and I'll point you to what the handbook says.\n\nI can only answer from the handbook itself — for anything about your own situation, your advisor or class dean is the right person to talk to.",
};

/* ---------------------------------------------------------------------------
 * Minimal markdown rendering: **bold**, bullet lists, and paragraphs.
 * Avoids pulling in a markdown dependency for a public-facing bundle.
 * ------------------------------------------------------------------------- */
function renderInline(text, keyPrefix) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return (
        <strong key={`${keyPrefix}-b${i}`} style={{ color: KA_NAVY }}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={`${keyPrefix}-t${i}`}>{part}</React.Fragment>;
  });
}

function MessageBody({ text }) {
  const lines = String(text).split("\n");
  const blocks = [];
  let bullets = [];

  const flushBullets = (idx) => {
    if (!bullets.length) return;
    blocks.push(
      <ul key={`ul-${idx}`} style={{ margin: "6px 0 6px 18px", padding: 0 }}>
        {bullets.map((b, i) => (
          <li key={`li-${idx}-${i}`} style={{ marginBottom: 4, lineHeight: 1.55 }}>
            {renderInline(b, `li-${idx}-${i}`)}
          </li>
        ))}
      </ul>
    );
    bullets = [];
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    if (/^[-*•]\s+/.test(trimmed)) {
      bullets.push(trimmed.replace(/^[-*•]\s+/, ""));
      return;
    }
    if (/^\d+\.\s+/.test(trimmed)) {
      bullets.push(trimmed.replace(/^\d+\.\s+/, ""));
      return;
    }
    flushBullets(idx);
    if (!trimmed) return;

    // Section citation line, e.g. "— See: Mobile Phones"
    if (/^(—|--)\s*See:/i.test(trimmed)) {
      blocks.push(
        <p
          key={`cite-${idx}`}
          style={{
            margin: "10px 0 0",
            paddingTop: 8,
            borderTop: "1px solid #E8E4DC",
            fontSize: 13,
            color: KA_GOLD,
            fontStyle: "italic",
          }}
        >
          {trimmed.replace(/^(—|--)\s*/, "")}
        </p>
      );
      return;
    }

    blocks.push(
      <p key={`p-${idx}`} style={{ margin: "0 0 8px", lineHeight: 1.6 }}>
        {renderInline(trimmed, `p-${idx}`)}
      </p>
    );
  });

  flushBullets("end");
  return <div>{blocks}</div>;
}

/* ------------------------------------------------------------------------- */

export default function HandbookBot() {
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = useCallback(
    async (textOverride) => {
      const question = String(textOverride ?? input).trim();
      if (!question || loading) return;

      setError("");
      setInput("");

      const nextMessages = [...messages, { role: "user", content: question }];
      setMessages(nextMessages);
      setLoading(true);

      try {
        // Drop the local greeting; the API only needs the real exchange.
        const payload = nextMessages
          .filter((m) => m !== GREETING)
          .map((m) => ({ role: m.role, content: m.content }));

        const res = await fetch(ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ messages: payload }),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(
            data.error || "The assistant is unavailable right now."
          );
        }

        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.reply },
        ]);
      } catch (err) {
        setError(err.message || "Something went wrong. Please try again.");
      } finally {
        setLoading(false);
        if (inputRef.current) inputRef.current.focus();
      }
    },
    [input, loading, messages]
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const resetChat = () => {
    setMessages([GREETING]);
    setError("");
    setInput("");
  };

  /* ---------------------------- PDF export ------------------------------ */
  const exportPDF = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 54;
    const contentW = pageW - margin * 2;
    let y = 0;

    const hexToRgb = (hex) => [
      parseInt(hex.slice(1, 3), 16),
      parseInt(hex.slice(3, 5), 16),
      parseInt(hex.slice(5, 7), 16),
    ];
    const [nr, ng, nb] = hexToRgb(KA_NAVY);
    const [rr, rg, rb] = hexToRgb(KA_RED);
    const [gr, gg, gb] = hexToRgb(KA_GOLD);

    const drawHeader = () => {
      doc.setFillColor(nr, ng, nb);
      doc.rect(0, 0, pageW, 64, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("times", "bold");
      doc.setFontSize(16);
      doc.text("King's Academy", margin, 28);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text("Student Handbook Assistant — conversation transcript", margin, 46);
      y = 92;
    };

    const drawFooter = (pageNum) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(140, 140, 140);
      doc.text(
        "Generated by the King's Academy Student Handbook Assistant. AI-generated — confirm details with your advisor or class dean.",
        margin,
        pageH - 32,
        { maxWidth: contentW }
      );
      doc.text(String(pageNum), pageW - margin, pageH - 32, { align: "right" });
    };

    const ensureSpace = (needed) => {
      if (y + needed > pageH - 60) {
        drawFooter(doc.internal.getNumberOfPages());
        doc.addPage();
        drawHeader();
      }
    };

    drawHeader();

    // Date line
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text(
      new Date().toLocaleString("en-GB", {
        dateStyle: "full",
        timeStyle: "short",
      }),
      margin,
      y
    );
    y += 24;

    const transcript = messages.filter((m) => m !== GREETING);

    if (!transcript.length) {
      doc.setFontSize(11);
      doc.setTextColor(90, 90, 90);
      doc.text("No questions were asked in this conversation.", margin, y);
    }

    transcript.forEach((m) => {
      const isUser = m.role === "user";
      const label = isUser ? "Question" : "Handbook Assistant";

      ensureSpace(40);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      if (isUser) doc.setTextColor(rr, rg, rb);
      else doc.setTextColor(nr, ng, nb);
      doc.text(label, margin, y);
      y += 15;

      // Strip markdown for print
      const clean = String(m.content)
        .replace(/\*\*/g, "")
        .replace(/^[-*•]\s+/gm, "•  ")
        .replace(/\n{3,}/g, "\n\n");

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10.5);
      doc.setTextColor(40, 40, 40);

      const lines = doc.splitTextToSize(clean, contentW - (isUser ? 12 : 0));
      lines.forEach((line) => {
        ensureSpace(16);
        if (isUser) {
          doc.setDrawColor(gr, gg, gb);
          doc.setLineWidth(2);
          doc.line(margin - 8, y - 9, margin - 8, y + 3);
        }
        doc.text(line, margin + (isUser ? 4 : 0), y);
        y += 15;
      });

      y += 14;
    });

    drawFooter(doc.internal.getNumberOfPages());

    const stamp = new Date().toISOString().slice(0, 10);
    doc.save(`KA-Handbook-Conversation-${stamp}.pdf`);
  };

  const hasConversation = messages.some((m) => m !== GREETING);

  /* ------------------------------ styles -------------------------------- */
  const s = {
    page: {
      minHeight: "100vh",
      background: "#F7F5F1",
      fontFamily:
        "'Helvetica Neue', Helvetica, Arial, sans-serif",
      display: "flex",
      flexDirection: "column",
    },
    header: {
      background: KA_NAVY,
      color: "#fff",
      padding: "18px 24px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 12,
    },
    title: {
      fontFamily: "Georgia, 'Times New Roman', serif",
      fontSize: 22,
      margin: 0,
      fontWeight: 600,
    },
    subtitle: { fontSize: 13, opacity: 0.8, margin: "3px 0 0" },
    headerBtns: { display: "flex", gap: 8 },
    ghostBtn: {
      background: "transparent",
      border: "1px solid rgba(255,255,255,0.45)",
      color: "#fff",
      padding: "7px 14px",
      borderRadius: 4,
      fontSize: 13,
      cursor: "pointer",
    },
    shell: {
      flex: 1,
      maxWidth: 860,
      width: "100%",
      margin: "0 auto",
      padding: "20px 16px 0",
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
    },
    scroll: {
      flex: 1,
      overflowY: "auto",
      paddingRight: 4,
      minHeight: 300,
    },
    bubbleUser: {
      background: KA_NAVY,
      color: "#fff",
      padding: "12px 16px",
      borderRadius: "12px 12px 2px 12px",
      maxWidth: "82%",
      fontSize: 15,
      lineHeight: 1.55,
      whiteSpace: "pre-wrap",
    },
    bubbleBot: {
      background: "#fff",
      color: "#2A2A2A",
      padding: "14px 18px",
      borderRadius: "12px 12px 12px 2px",
      maxWidth: "88%",
      fontSize: 15,
      border: "1px solid #E8E4DC",
      borderLeft: `3px solid ${KA_GOLD}`,
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    },
    chip: {
      background: "#fff",
      border: "1px solid #DED8CC",
      color: KA_NAVY,
      padding: "8px 14px",
      borderRadius: 18,
      fontSize: 13.5,
      cursor: "pointer",
      textAlign: "left",
    },
    composer: {
      background: "#fff",
      border: "1px solid #DED8CC",
      borderRadius: 10,
      padding: 10,
      display: "flex",
      gap: 10,
      alignItems: "flex-end",
      margin: "12px 0 6px",
    },
    textarea: {
      flex: 1,
      border: "none",
      outline: "none",
      resize: "none",
      fontSize: 15,
      fontFamily: "inherit",
      lineHeight: 1.5,
      maxHeight: 140,
      background: "transparent",
      color: "#2A2A2A",
    },
    sendBtn: {
      background: loading || !input.trim() ? "#C9C3B8" : KA_RED,
      color: "#fff",
      border: "none",
      borderRadius: 6,
      padding: "10px 20px",
      fontSize: 14,
      fontWeight: 600,
      cursor: loading || !input.trim() ? "default" : "pointer",
      whiteSpace: "nowrap",
    },
    disclaimer: {
      fontSize: 11.5,
      color: "#8A8578",
      textAlign: "center",
      padding: "0 0 18px",
      lineHeight: 1.5,
    },
  };

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div>
          <h1 style={s.title}>Student Handbook Assistant</h1>
          <p style={s.subtitle}>King's Academy · Madaba, Jordan</p>
        </div>
        <div style={s.headerBtns}>
          <button
            style={{
              ...s.ghostBtn,
              opacity: hasConversation ? 1 : 0.45,
              cursor: hasConversation ? "pointer" : "default",
            }}
            onClick={hasConversation ? exportPDF : undefined}
            disabled={!hasConversation}
          >
            Print / Save PDF
          </button>
          <button style={s.ghostBtn} onClick={resetChat}>
            New conversation
          </button>
        </div>
      </header>

      <div style={s.shell}>
        <div style={s.scroll} ref={scrollRef}>
          {messages.map((m, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: m.role === "user" ? "flex-end" : "flex-start",
                marginBottom: 16,
              }}
            >
              <div style={m.role === "user" ? s.bubbleUser : s.bubbleBot}>
                {m.role === "user" ? (
                  m.content
                ) : (
                  <MessageBody text={m.content} />
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div style={{ display: "flex", marginBottom: 16 }}>
              <div style={{ ...s.bubbleBot, color: "#8A8578", fontSize: 14 }}>
                Looking through the handbook…
              </div>
            </div>
          )}

          {error && (
            <div
              style={{
                background: "#FDECEC",
                border: `1px solid ${KA_RED}`,
                color: "#9B2220",
                padding: "10px 14px",
                borderRadius: 8,
                fontSize: 14,
                marginBottom: 16,
              }}
            >
              {error}
            </div>
          )}

          {!hasConversation && !loading && (
            <div style={{ marginBottom: 20 }}>
              <p
                style={{
                  fontSize: 12.5,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "#8A8578",
                  marginBottom: 10,
                }}
              >
                Try asking
              </p>
              <div
                style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
              >
                {SUGGESTED.map((q) => (
                  <button key={q} style={s.chip} onClick={() => send(q)}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={s.composer}>
          <textarea
            ref={inputRef}
            style={s.textarea}
            rows={1}
            value={input}
            maxLength={MAX_CHARS}
            placeholder="Ask a question about the handbook…"
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = `${Math.min(e.target.scrollHeight, 140)}px`;
            }}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
          <button
            style={s.sendBtn}
            onClick={() => send()}
            disabled={loading || !input.trim()}
          >
            {loading ? "…" : "Ask"}
          </button>
        </div>

        <p style={s.disclaimer}>
          AI-generated answers based on the Student Handbook (Final Draft, August 3, 2026).
          The handbook itself is the authoritative source. For questions about your own
          situation, please speak with your advisor, class dean, or the dean of students.
        </p>
      </div>
    </div>
  );
}